import { Link, Navigate, useParams } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { EU_PROGRAMMES } from "@/data/euProgrammes";

/**
 * Illustrative Sourcing Programme detail page — /programmes/:slug
 *
 * These are capability-based illustrative programmes, NOT real client
 * engagements. Every page carries the "Illustrative Sourcing Programme"
 * label prominently at the top and repeats the disclaimer in the FAQ.
 * Distinct from /case-studies/* (long-form real programmes with
 * anonymised clients) and /work/* (100-word outcome cards).
 */
export const ProgrammeDetail = () => {
  const { slug } = useParams();
  const page = EU_PROGRAMMES.find((p) => p.slug === slug);
  const { open } = useStartProject();

  useSeo({
    title: page ? page.title : "Illustrative Sourcing Programme",
    description: page ? page.metaDescription : undefined,
    path: page ? `/programmes/${page.slug}` : "/programmes",
    type: "article",
    jsonLd: page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Article",
              "@id": `${SITE_URL}/programmes/${page.slug}#article`,
              headline: page.h1,
              description: page.metaDescription,
              articleSection: page.category,
              inLanguage: "en",
              author: { "@id": `${SITE_URL}#organization` },
              publisher: { "@id": `${SITE_URL}#organization` },
              mainEntityOfPage: `${SITE_URL}/programmes/${page.slug}`,
              about: [
                { "@type": "Country", name: page.country },
                { "@type": "Thing", name: page.category },
              ],
              // Explicit disclosure: this is an illustrative educational piece
              disambiguatingDescription:
                "Illustrative sourcing programme — capability-based educational content, not a real client engagement.",
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}/programmes/${page.slug}#faq`,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Illustrative Programmes", path: "/programmes" },
              { name: page.category, path: `/programmes/${page.slug}` },
            ]),
          ],
        }
      : null,
    jsonLdId: "programme-detail-jsonld",
  });

  if (!page) return <Navigate to="/programmes" replace />;

  // Sibling programmes in the same country for cross-linking
  const siblings = EU_PROGRAMMES.filter(
    (p) => p.country === page.country && p.slug !== page.slug
  );
  const others = EU_PROGRAMMES.filter(
    (p) => p.country !== page.country
  ).slice(0, 3);

  return (
    <article data-testid="programme-detail-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="programme-crumb-home">
              Home
            </Link>{" "}
            ·{" "}
            <Link to="/programmes" style={{ color: "inherit" }} data-testid="programme-crumb-programmes">
              Illustrative Programmes
            </Link>{" "}
            · {page.category}
          </p>
        </Reveal>

        {/* Explicit illustrative-label banner */}
        <Reveal>
          <div
            data-testid="programme-illustrative-banner"
            style={{
              display: "inline-block",
              padding: ".5rem 1rem",
              border: "1px solid var(--brass-deep)",
              color: "var(--brass-deep)",
              fontSize: ".72rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              fontFamily: "var(--mono)",
              marginBottom: "1rem",
            }}
          >
            {page.label} · Not a real client engagement
          </div>
        </Reveal>

        <Reveal>
          <p className="eyebrow" data-testid="programme-eyebrow">
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="programme-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="programme-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="programme-cta-primary"
          >
            Start Your Project →
          </button>
          <Link
            to={page.relatedLinks.country}
            className="btn btn-ghost"
            data-testid="programme-cta-country"
          >
            {page.country} programmes
          </Link>
        </Reveal>
      </header>

      {/* KEY FACTS */}
      <section
        className="wrap"
        data-testid="programme-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {page.keyFacts.map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {page.sections.map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`programme-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {s.body.map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Relevant Asia Sourcing services block */}
        <Reveal as="section" style={{ marginTop: "2.6rem" }} data-testid="programme-related-services">
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
            }}
          >
            Relevant Asia Sourcing services
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {[
              { name: `${page.country} programmes`, slug: page.relatedLinks.country, testid: "programme-link-country" },
              { name: `${page.category} category`, slug: page.relatedLinks.category, testid: "programme-link-category" },
              { name: "India sourcing service", slug: page.relatedLinks.indiaCategory, testid: "programme-link-service" },
              { name: "Supplier Verification", slug: page.relatedLinks.verification, testid: "programme-link-verification" },
              { name: "Factory Audit", slug: page.relatedLinks.audit, testid: "programme-link-audit" },
              { name: "Quality Inspection", slug: page.relatedLinks.qc, testid: "programme-link-qc" },
            ].map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={r.testid}
                style={{
                  display: "block",
                  padding: "1.1rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p style={{ fontFamily: "var(--display)", fontSize: "1rem", margin: 0 }}>
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="programme-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          Frequently asked questions
        </h2>
        {page.faqs.map(([q, a], i) => (
          <details
            key={i}
            data-testid={`programme-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* Sibling programmes cross-links */}
      {siblings.length > 0 && (
        <section
          className="wrap"
          data-testid="programme-siblings"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        >
          <p
            className="mono"
            style={{
              color: "var(--brass-deep)",
              fontSize: ".68rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              margin: "0 0 .8rem",
            }}
          >
            Other illustrative programmes for {page.country} buyers
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
            }}
          >
            {siblings.map((s, i) => (
              <Link
                key={i}
                to={`/programmes/${s.slug}`}
                data-testid={`programme-sibling-${s.slug}`}
                style={{
                  display: "block",
                  padding: "1.1rem 1.2rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .3rem",
                  }}
                >
                  {s.market} · {s.category}
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: 0 }}>
                  {s.h1.substring(0, 60)}… →
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Other market programmes */}
      {others.length > 0 && (
        <section
          className="wrap"
          data-testid="programme-others"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        >
          <p
            className="mono"
            style={{
              color: "var(--brass-deep)",
              fontSize: ".68rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              margin: "0 0 .8rem",
            }}
          >
            Programmes for other European markets
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
            }}
          >
            {others.map((o, i) => (
              <Link
                key={i}
                to={`/programmes/${o.slug}`}
                data-testid={`programme-other-${o.slug}`}
                style={{
                  display: "block",
                  padding: "1.1rem 1.2rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .3rem",
                  }}
                >
                  {o.market} · {o.category}
                </p>
                <p style={{ fontFamily: "var(--display)", fontSize: "1.05rem", margin: 0 }}>
                  {o.h1.substring(0, 60)}… →
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        data-testid="programme-enquiry"
      >
        <Reveal as="p" className="eyebrow">
          Start an enquiry
        </Reveal>
        <Reveal as="h2" className="display" style={{ margin: "1rem 0 2rem" }}>
          Talk to Asia Sourcing about a similar programme.
        </Reveal>
        <Reveal
          as="div"
          style={{ display: "flex", flexWrap: "wrap", gap: "1rem", marginTop: "1rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="programme-cta-enquiry"
          >
            Start Your Project →
          </button>
          <Link to="/contact" className="btn btn-ghost" data-testid="programme-cta-contact">
            Send an enquiry
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

export default ProgrammeDetail;
